import ConsumerRefService from "@api/consumer/consumerRefService";
import { IGoalCondition } from "@interfaces/consumer/refs";
import { dateFormatter } from "@services/helpers";
import { FC, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ColumnDef } from "@tanstack/react-table";
import { useRemoteTable } from "@hooks/tableHooks";
import { Card, Form } from "react-bootstrap";
import { Flex, IconButton } from "@components/falcon/common";
import { AdvanceTable, AdvanceTableFooter, AdvanceTableSearchBox } from "@components/falcon/common/tables";
import AdvanceTableProvider from "@components/falcon/common/tables/context";
import { useRole } from "@services/hooks";

const columns: ColumnDef<IGoalCondition>[] = [
    {
        accessorKey: 'name',
        header: 'Название',
        enableSorting: true,
        meta: {
            headerProps: { className: 'pe-1 text-900' },
            cellProps: { className: 'py-2' }
        },
        cell: ({ row: { original: { id, goalId, name } } }) => (
            <Link to={`/consumer/goals/${goalId}/conditions/${id}/`}>
                <Flex alignItems="center" className="flex-1">
                    <h5 className="mb-0 fs-10">{name}</h5>
                </Flex>
            </Link>
        )
    },
    {
        accessorKey: 'createTime',
        header: 'Дата создания',
        enableSorting: true,
        cell: ({ row: { original: { createTime } } }) => dateFormatter(createTime),
    },
    {
        accessorKey: 'isActive',
        header: 'Активно',
        enableSorting: false,
        meta: {
            headerProps: { className: 'text-center text-900' },
            cellProps: { className: 'text-center' }
        },
        cell: ({ row: { original: { isActive } } }) => (
            <div className="text-center">
                <Form.Check type="checkbox" defaultChecked={isActive} disabled />
            </div>
        )
    },
    {
        accessorKey: 'type',
        header: 'Тип',
        enableSorting: false,
        cell: ({ row: { original: { type } } }) => type,
    }
]

const GoalConditionsList: FC = () => {

    const [api] = useState(() => new ConsumerRefService());
    const navigate = useNavigate();
    const { goal_id } = useParams<{ goal_id: string }>();

    const table = useRemoteTable({
        name: `consumer.goals.${goal_id}.conditions`,
        columns,
        defaultState: {
            sorting: [{ id: 'name', desc: false }]
        }
    }, (model) => api.getGoalConditions(goal_id || '', model));

    const canEdit = useRole('consumer.goal.edit');

    return (
        <AdvanceTableProvider table={table}>
            <Card className="mb-3">
                <Card.Header>
                    <Flex className='align-items-center justify-content-between'>
                        <Flex alignItems="center" className="fs-10">
                            <h5 className="fs-9 mb-0 text-nowrap py-2 py-xl-0">Список условий</h5>
                        </Flex>
                        <Flex>
                            {canEdit && (
                                <IconButton
                                    variant="falcon-default"
                                    size="sm"
                                    icon="plus"
                                    transform="shrink-3"
                                    className='me-2'
                                    onClick={() => navigate(`/consumer/goals/${goal_id}/conditions/add/`)}
                                >
                                    <span className="d-none d-sm-inline-block ms-1">Создать</span>
                                </IconButton>
                            )}
                            <Flex><AdvanceTableSearchBox /></Flex>
                        </Flex>
                    </Flex>
                </Card.Header>
                <Card.Body className="p-0">
                    <AdvanceTable
                        headerClassName="bg-200 text-nowrap align-middle"
                        rowClassName="align-middle white-space-nowrap"
                        tableProps={{
                            size: 'sm',
                            striped: true,
                            className: 'fs-10 mb-0 overflow-hidden'
                        }}
                    />
                </Card.Body>
                <Card.Footer>
                    <AdvanceTableFooter
                        rowInfo
                        rowsPerPageSelection
                        paginationButtons
                    />
                </Card.Footer>
            </Card>
        </AdvanceTableProvider>
    )
}

export default GoalConditionsList;
